/**
 * Layout shell for Profit Report when live data is loading or absent.
 * Keeps cards, chart, and table headers on screen — no fake £0 totals.
 */

import { profitChartTitle } from './profitReportPeriodRange';
import type { ProfitPageBundle, ProfitReportPeriod } from '../data/mockProfitPage';


function vehicleTable(): ProfitPageBundle['detail']['vehicle'] {
  const heads = [
    'Date',
    'Vehicle',
    'Type',
    'Miles',
    'Pickup',
    'Delivery',
    'Jobs',
    'Revenue',
    'Fuel',
    'Driver',
    'Stand.',
    'Profit',
    'Margin',
  ];
  return {
    heads,
    rows: [],
    foot: ['—', ...Array(heads.length - 1).fill('')],
    moneyCols: [7, 8, 9, 10, 11, 12],
    typeCol: 2,
    footLabelColSpan: 2,
  };
}

function jobTable(): ProfitPageBundle['detail']['job'] {
  const heads = [
    'Tracking ID',
    'Customer',
    'Vehicle',
    'Lifts',
    'Revenue',
    'Own cost',
    'Profit',
    'Margin',
  ];
  return {
    heads,
    rows: [],
    foot: ['—', ...Array(heads.length - 1).fill('')],
    moneyCols: [4, 5, 6, 7],
  };
}

function customerTable(): ProfitPageBundle['detail']['customer'] {
  const heads = ['Customer', 'Jobs', 'Lifts', 'Revenue', 'Own cost', 'Profit', 'Margin'];
  return {
    heads,
    rows: [],
    foot: ['—', ...Array(heads.length - 1).fill('')],
    moneyCols: [3, 4, 5, 6],
  };
}

export function emptyProfitReportShell(
  period: ProfitReportPeriod,
  range: { startDate: string; endDate: string },
  demo: boolean,
): ProfitPageBundle {
  return {
    sourcesLabel: 'Xero · Palletways · Samsara',
    kpiMode: demo ? 'demo_xero_style' : 'ops_attributed',
    kpis: {
      netProfitGbp: 0,
      marginPct: 0,
      retainedRevenueGbp: 0,
      customerRevenueGbp: 0,
      networkChargesGbp: 0,
      ownCostsGbp: 0,
      fuelGbp: 0,
      driversGbp: 0,
      standingGbp: 0,
      workingDays: 0,
      stampLabel: `${range.startDate} → ${range.endDate}`,
    },
    dailySeries: [],
    chartTitle: profitChartTitle(period),
    dataQuality: null,
    detail: {
      vehicle: vehicleTable(),
      job: jobTable(),
      customer: customerTable(),
      postcode: {
        heads: ['Period', 'Postcode district', 'Note'],
        rows: [],
        foot: ['', '', ''],
        moneyCols: [],
      },
      run: {
        heads: ['Period', 'Run', 'Note'],
        rows: [],
        foot: ['', '', ''],
        moneyCols: [],
        runPillCol: 1,
      },
    },
    notes: {
      freshness:
        'Ops attribution refreshes when profitability projections recompute. Figures are only as current as the last successful compute for this window.',
      dataQualityFollowUp:
        'Coverage gaps mean missing vehicle-days, not £0 profit. Xero books are Phase 2 — these KPIs must not be read as retained revenue from the ledger.',
      kpis:
        'Attributed revenue is TMS / vehicle-day revenue from truth allocation. Network charges are not separated here. Margin is profit ÷ attributed revenue.',
      detail:
        'Vehicle rows from merged-costs; jobs from merged-orders; customers from customer-profitability-daily.',
    },
  };
}
